// CardAudioSynthesizer.ts - Web Audio API procedural synthesis for card interactions
import { AudioSynthHelper } from './AudioSynthHelper';

export class CardAudioSynthesizer {
  /**
   * Deep, velvety card placement onto green casino baize felt with clear mobile speaker presence.
   */
  public static playCardToFelt(ctx: AudioContext) {
    const t = ctx.currentTime;

    // Layer 1: Warm, soft felt body impact (smooth sine thump without harsh triangle click harmonics)
    AudioSynthHelper.playTone(ctx, {
      freq: 140,
      endFreq: 60,
      start: t,
      dur: 0.08,
      type: 'sine',
      gain: 0.28,
      attack: 0.008,
    });

    // Layer 2: Deep sub-felt table impact thud (for headphones & bass-capable speakers)
    AudioSynthHelper.playTone(ctx, {
      freq: 72,
      endFreq: 30,
      start: t,
      dur: 0.11,
      type: 'sine',
      gain: 0.36,
      attack: 0.006,
    });

    // Layer 3: Warm, plush velvet cushion & air compression
    AudioSynthHelper.playNoiseBurst(ctx, {
      start: t,
      dur: 0.065,
      decay: 0.024,
      gain: 0.22,
      attack: 0.004,
      filter: { type: 'lowpass', freq: 500, endFreq: 160 },
    });
  }

  /**
   * Crisp, authentic casino card snap / slap on table
   */
  public static playCardSnap(ctx: AudioContext) {
    const t = ctx.currentTime;

    // Layer 1: High-friction paper/plastic flick & whip
    AudioSynthHelper.playNoiseBurst(ctx, {
      start: t,
      dur: 0.05,
      decay: 0.012,
      gain: 0.42,
      attack: 0.003,
      filter: { type: 'bandpass', freq: 2400, endFreq: 900, Q: 2.0 },
    });

    // Layer 2: Fast tonal snap body
    AudioSynthHelper.playTone(ctx, {
      freq: 520,
      endFreq: 140,
      start: t,
      dur: 0.035,
      type: 'triangle',
      gain: 0.35,
      attack: 0.003,
    });

    // Layer 3: Warm table felt thud
    AudioSynthHelper.playTone(ctx, {
      freq: 160,
      endFreq: 55,
      start: t,
      dur: 0.06,
      type: 'sine',
      gain: 0.36,
      attack: 0.004,
    });
  }

  /**
   * Procedural Riffle Shuffle sound (rapid interlacing flutter + waterfall bridge)
   */
  public static playRiffleShuffle(ctx: AudioContext, snappy: boolean = true) {
    const t = ctx.currentTime;
    const cardClicks = snappy ? 20 : 28;
    const flutterDuration = snappy ? 0.36 : 0.75;

    for (let i = 0; i < cardClicks; i++) {
      const progress = i / cardClicks;
      const clickTime = t + 0.02 + progress * flutterDuration + (Math.random() * 0.008 - 0.004);
      const baseFreq = 480 + Math.sin(progress * Math.PI) * 260 + (Math.random() * 80 - 40);

      AudioSynthHelper.playTone(ctx, {
        freq: baseFreq,
        endFreq: 120,
        start: clickTime,
        dur: 0.025,
        type: 'triangle',
        gain: 0.18 * (0.6 + 0.4 * Math.sin(progress * Math.PI)),
        filter: { type: 'bandpass', freq: 1800, Q: 3.0 },
      });
    }

    // Cascade Waterfall Bridge Snap
    const bridgeTime = t + (snappy ? 0.40 : 0.85);
    AudioSynthHelper.playTone(ctx, {
      freq: 360,
      endFreq: 80,
      start: bridgeTime,
      dur: 0.20,
      type: 'sine',
      gain: 0.24,
    });

    // Deck square-up tap
    const tapTime = t + (snappy ? 0.54 : 1.15);
    AudioSynthHelper.playTone(ctx, {
      freq: 420,
      endFreq: 110,
      start: tapTime,
      dur: 0.05,
      type: 'triangle',
      gain: 0.22,
    });
  }

  /**
   * Clean deck cut and re-stack sound
   */
  public static playDeckCut(ctx: AudioContext) {
    const t = ctx.currentTime;
    [0.0, 0.08].forEach((offset, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq: idx === 0 ? 380 : 280,
        endFreq: 90,
        start: t + offset,
        dur: 0.055,
        type: idx === 0 ? 'triangle' : 'sine',
        gain: 0.20,
      });
    });
  }

  /**
   * Satisfying felt tuck sound when dealing a card into the center Kitty
   */
  public static playKittyTuck(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 540,
      endFreq: 180,
      dur: 0.065,
      type: 'triangle',
      gain: 0.22,
    });
  }

  /**
   * Card deal / slide sound across green felt
   */
  public static playCardDeal(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 520,
      endFreq: 180,
      dur: 0.065,
      type: 'sine',
      gain: 0.20,
      attack: 0.004,
    });
  }

  /**
   * Authentic casino card fanning sound (sample or acoustic friction fallback)
   */
  public static playCardFan(
    ctx: AudioContext,
    cardFanBuffer: AudioBuffer | null,
    duration?: number,
    _cardCount?: number
  ) {
    if (cardFanBuffer) {
      try {
        const source = ctx.createBufferSource();
        const gain = ctx.createGain();
        source.buffer = cardFanBuffer;
        source.playbackRate.value = 1.05;
        gain.gain.setValueAtTime(0.70, ctx.currentTime);

        source.connect(gain);
        gain.connect(AudioSynthHelper.getDestination(ctx));
        source.start(ctx.currentTime);
        return;
      } catch (e) {
        console.warn('[CardAudioSynthesizer] Error playing card fan sample', e);
      }
    }

    // Acoustic friction fallback
    const totalTime = duration || 0.75;
    AudioSynthHelper.playNoiseBurst(ctx, {
      dur: totalTime,
      decay: 0.4,
      gain: 0.14,
      attack: 0.04,
      filter: { type: 'bandpass', freq: 1600, endFreq: 900, Q: 1.8 },
    });
  }

  /**
   * Subtle high-tactile click when tapping/selecting a card
   */
  public static playCardSelect(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 600,
      endFreq: 900,
      dur: 0.045,
      type: 'sine',
      gain: 0.12,
      attack: 0.003,
    });
  }

  /**
   * Discard pop sound when unselecting or selecting a kitty discard
   */
  public static playCardDiscard(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 400,
      endFreq: 180,
      dur: 0.08,
      type: 'triangle',
      gain: 0.2,
      attack: 0.004,
    });
  }

  /**
   * Quick muted double-buzz when attempting an illegal card play
   */
  public static playIllegalCard(ctx: AudioContext) {
    AudioSynthHelper.playTone(ctx, {
      freq: 140,
      endFreq: 65,
      dur: 0.10,
      type: 'sawtooth',
      gain: 0.22,
    });
  }
}
